import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ReactPaginate from "react-paginate";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { postData } from "../../../utility/Utility";
import Swal from "sweetalert2";
import CustomLoader from "../../../Components/CustomLoader/CustomLoader";

function TeamMemberList() {
  const navigate = useNavigate();
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const rowsPerPage = 10;

  useEffect(() => {
    fetchTeamMembers(currentPage + 1, searchTerm);
  }, [currentPage, searchTerm]);

  const fetchTeamMembers = async (page = 1, search = "") => {
    setLoading(true);
    try {
      const response = await postData("api/team-members/list", {
        search: search,
        page: page,
        limit: rowsPerPage,
      });

      if (response?.success) {
        setTeamMembers(response.teamMembers || []);
        setTotalPages(response.totalPages || 0);
        
        console.log('Team Members API Response:', {
          success: response.success,
          count: response.count,
          total: response.total,
          page: response.page,
          totalPages: response.totalPages,
          teamMembersLength: response.teamMembers?.length || 0
        });
      } else {
        toast.error(response?.message || "Failed to fetch team members");
      }
    } catch (error) {
      toast.error("Error fetching team members");
      console.error("Fetch team members error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = ({ selected }) => {
    setCurrentPage(selected);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(0);
    fetchTeamMembers(1, searchTerm);
  };

  const handleReset = () => {
    setSearchTerm("");
    setCurrentPage(0);
    fetchTeamMembers(1);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await postData(`api/team-members/delete`, { id });
        if (response.success) {
          toast.success("Team member deleted successfully");
          fetchTeamMembers(currentPage + 1, searchTerm);
        } else {
          toast.error(response.message || "Failed to delete team member");
        }
      } catch (error) {
        toast.error("Error deleting team member");
        console.error("Delete team member error:", error);
      }
    }
  };

  const handleStatusChange = async (member) => {
    const newStatus = member.status === "active" ? "inactive" : "active";

    const result = await Swal.fire({
      title: `Are you sure you want to ${newStatus === "active" ? "activate" : "deactivate"} this team member?`,
      text: `The team member will be ${newStatus === "active" ? "activated" : "deactivated"}.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, update it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await postData(`api/team-members/update`, {
          id: member._id,
          status: newStatus,
        });
        if (response.success) {
          toast.success(`Team member ${newStatus === "active" ? "activated" : "deactivated"} successfully`);
          fetchTeamMembers(currentPage + 1, searchTerm);
        } else {
          toast.error(response.message || `Failed to ${newStatus === "active" ? "activate" : "deactivate"} team member`);
        }
      } catch (error) {
        toast.error("Error updating team member status");
        console.error("Update team member status error:", error);
      }
    }
  };

  const getMemberTypeLabel = (memberType) => {
    const types = {
      "KP Startup Hub Board Members": "KP Startup Hub Board",
      "Operational Team": "Operational Team",
      "IT Team": "IT Team",
    };
    return types[memberType] || memberType;
  };

  return (
    <div className="content-page">
      <ToastContainer position="top-right" autoClose={5000} />
      <CustomLoader loading={loading} />
      <div className="content">
        <div className="container-fluid">
          <div className="py-3 d-flex align-items-sm-center flex-sm-row flex-column justify-content-between">
            <div className="flex-grow-1">
              <div className="page-title-box">
                <h4 className="mb-1">Team Member Management</h4>
                <ol className="breadcrumb m-0">
                  <li className="breadcrumb-item">Team Member</li>
                  <li className="breadcrumb-item active">Team Member List</li>
                </ol>
              </div>
            </div>
            <div>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => navigate("/add-team-member")}
                disabled={loading}
              >
                <i className="fas fa-plus me-1"></i> Add Team Member
              </button>
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-header">
                  <h5 className="card-title mb-0">Team Members</h5>
                </div>
                <div className="card-body">
                  {/* Search Form */}
                  <form onSubmit={handleSubmit} className="mb-4">
                    <div className="row g-3">
                      <div className="col-md-8">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search by name..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </div>
                      <div className="col-md-2">
                        <button type="submit" className="btn btn-primary w-100">
                          Search
                        </button>
                      </div>
                      <div className="col-md-2">
                        <button
                          type="button"
                          className="btn btn-outline-secondary w-100"
                          onClick={handleReset}
                        >
                          Reset
                        </button>
                      </div>
                    </div>
                  </form>

                  {loading ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead>
                          <tr>
                            <th>S.No</th>
                            <th>Image</th>
                            <th>Name</th>
                            <th>Designation</th>
                            <th>Member Type</th>
                            <th>Order</th>
                            <th>Status</th>
                            <th>Created At</th>
                            <th width="150px">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {teamMembers.length > 0 ? (
                            teamMembers.map((member, index) => (
                              <tr key={member._id}>
                                <td>{currentPage * rowsPerPage + index + 1}</td>
                                <td>
                                  <img
                                    src={member.image}
                                    alt={member.teamMemberName}
                                    style={{ width: 50, height: 50, objectFit: "cover", borderRadius: "50%" }}
                                  />
                                </td>
                                <td>{member.teamMemberName}</td>
                                <td>{member.designation}</td>
                                <td>
                                  <span className="badge bg-info">
                                    {getMemberTypeLabel(member.memberType)}
                                  </span>
                                </td>
                                <td>{member.order}</td>
                                <td>
                                  <span
                                    className={`badge text-capitalize bg-${member.status === "active" ? "success" : "danger"}`}
                                  >
                                    {member.status}
                                  </span>
                                </td>
                                <td>
                                  {new Date(member.createdAt).toLocaleDateString()}
                                </td>
                                <td>
                                  <div className="d-flex gap-2">
                                    <button
                                      className="btn btn-sm btn-outline-primary"
                                      onClick={() =>
                                        navigate(`/update-team-member`, {
                                          state: { memberData: member },
                                        })
                                      }
                                    >
                                      <i className="mdi mdi-pencil"></i>
                                    </button>
                                    <button
                                      className={`btn btn-sm btn-outline-${member.status === "active" ? "warning" : "success"}`}
                                      onClick={() => handleStatusChange(member)}
                                      title={member.status === "active" ? "Deactivate" : "Activate"}
                                    >
                                      <i className={`mdi mdi-${member.status === "active" ? "close" : "check"}`}></i>
                                    </button>
                                    <button
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() => handleDelete(member._id)}
                                    >
                                      <i className="mdi mdi-delete"></i>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            !loading && (
                              <tr>
                                <td colSpan="9" className="text-center py-4">
                                  No team members found
                                </td>
                              </tr>
                            )
                          )}
                        </tbody>
                      </table>

                      {totalPages > 1 && (
                        <ReactPaginate
                          previousLabel={<i className="mdi mdi-chevron-left"></i>}
                          nextLabel={<i className="mdi mdi-chevron-right"></i>}
                          breakLabel="..."
                          breakClassName="page-item"
                          breakLinkClassName="page-link"
                          pageCount={totalPages}
                          marginPagesDisplayed={2}
                          pageRangeDisplayed={5}
                          onPageChange={handlePageChange}
                          containerClassName="pagination pagination-rounded justify-content-end mt-3"
                          pageClassName="page-item"
                          pageLinkClassName="page-link"
                          previousClassName="page-item"
                          previousLinkClassName="page-link"
                          nextClassName="page-item"
                          nextLinkClassName="page-link"
                          activeClassName="active"
                          forcePage={currentPage}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TeamMemberList;
